import { parse, subscribe } from 'graphql';
import { MessageTypes } from './constants';
import { getExecutableSchema } from './executableSchema';
import { executeOperation } from './executeOperation';

const subscriptions = new Map<string, () => void>();

export type Messageable = {
  postMessage: (message: unknown, targetOrigin?: string) => void;
};

export type MessageDomElement = UXPWebviewElement | Window | Messageable;

export const handleGraphQLMessage = async (
  event: MessageEvent,
  webview: MessageDomElement | null
) => {
  if (!webview) {
    throw new Error('Webview is null');
  }
  const schema = getExecutableSchema();

  try {
    const { id, operation, variables } = event.data;

    const result = await executeOperation(schema, operation, variables);
    webview.postMessage(
      {
        type: MessageTypes.GRAPHQL_RESPONSE,
        id,
        result,
      },
      '*'
    );
  } catch (error) {
    webview.postMessage(
      {
        type: MessageTypes.GRAPHQL_ERROR,
        id: event.data?.id,
        error: {
          message: (error as Error).message,
          stack:
            process.env.NODE_ENV === 'development'
              ? (error as Error).stack
              : undefined,
        },
      },
      '*'
    );
  }
};

export const handleGraphQLSubscription = async (
  event: MessageEvent,
  webview: UXPWebviewElement | Window | Messageable | null
) => {
  if (!webview) {
    throw new Error('Webview is null');
  }
  const { id } = event.data;
  try {
    const schema = getExecutableSchema();

    const { operation, variables } = event.data;

    const parsedOperation =
      typeof operation === 'string' ? parse(operation) : operation;

    const asyncIterator = await subscribe({
      schema,
      document: parsedOperation,
      variableValues: variables,
    });

    if (asyncIterator && 'errors' in asyncIterator && asyncIterator.errors) {
      throw new Error(
        asyncIterator.errors[0]?.message || 'Subscription failed'
      );
    }

    const handleNext = async () => {
      try {
        const result = await (asyncIterator as AsyncIterator<any>).next();
        if (result.done) {
          webview.postMessage(
            {
              type: MessageTypes.GRAPHQL_SUBSCRIPTION_COMPLETE,
              id,
            },
            '*'
          );

          subscriptions.delete(id);
          return;
        }

        webview.postMessage(
          {
            type: MessageTypes.GRAPHQL_SUBSCRIPTION_DATA,
            id,
            result: result.value,
          },
          '*'
        );

        handleNext();
      } catch (error) {
        webview.postMessage(
          {
            type: MessageTypes.GRAPHQL_SUBSCRIPTION_ERROR,
            id,
            error: {
              message: (error as Error).message,
            },
          },
          '*'
        );

        subscriptions.delete(id);
      }
    };

    handleNext();

    subscriptions.set(id, () => {
      (asyncIterator as AsyncIterator<any>).return?.();
    });
  } catch (error) {
    webview.postMessage(
      {
        type: MessageTypes.GRAPHQL_SUBSCRIPTION_ERROR,
        id,
        error: {
          message: (error as Error).message,
        },
      },
      '*'
    );
  }
};

export const unsubscribeGraphQLSubscription = (id: string) => {
  const unsubscribe = subscriptions.get(id);
  if (unsubscribe) {
    unsubscribe();
    subscriptions.delete(id);
  }
};
